import 'rxjs/add/observable/combineLatest';

import {
  ActionSheetController,
  Content,
  IonicPage,
  ModalController,
  NavController,
  NavParams,
  Refresher,
} from 'ionic-angular';
import { Component, OnDestroy, ViewChild } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { interval } from 'rxjs/observable/interval';

import { AppModeProvider } from './../../providers/app-mode/app-mode';
import { EtaWarningModalComponent } from './../../components/eta-warning-modal/eta-warning-modal';
import { FillerShiftModel } from '../../models/filler-shift';
import { LoadingAlertProvider } from './../../providers/loading-alert/loading-alert';
import { OnTimePercentageModalComponent } from './../../components/on-time-percentage-modal/on-time-percentage-modal';
import { OpsOrderModel } from '../../models/order.ops';
import { OpsProvider } from '../../providers/ops/ops';
import { OrderDetailPage } from '../order-detail/order-detail';
import { ShiftPage } from './../../pages/shift/shift';
import { SimpleMessageModalComponent } from './../../components/simple-message-modal/simple-message-modal';
import { TruckModel } from '../../models/truck';
import { UserProvider } from './../../providers/user/user';

export enum OrderType {
  Pending = 'pending',
  Completed = 'completed',
}
@IonicPage()
@Component({
  selector: 'page-order-list',
  templateUrl: 'order-list.html',
})
export class OrderListPage implements OnDestroy {
  @ViewChild('ordersList') ordersList: Content;
  @ViewChild(Refresher) pullToRefresh: Refresher;
  truck: TruckModel;
  orders: OpsOrderModel[] = [];
  selectedOrders: OpsOrderModel[] = [];
  activePage: OrderType;
  title = 'Orders';
  showEdit = false;
  page = 1;
  infiniteScroll;
  isLoading: boolean;
  refresher: Refresher;
  currentFillerShift: FillerShiftModel;
  completedOnTimePercentage: number;
  showOnTimePercentage = false;
  readonly hasUpdatesToastText: string =
    'Route has changed. Tap to see updates';
  readonly REFRESH_ORDERS_INTERVAL: number = 15000;
  readonly SCROLL_ANIMATION_DURATION: number = 500;

  showFloatingRefresh = false;
  private unsubscribe: Subject<void> = new Subject();

  constructor(
    navParams: NavParams,
    private appMode: AppModeProvider,
    private navCtrl: NavController,
    private opsService: OpsProvider,
    private actionSheet: ActionSheetController,
    private loadingAlert: LoadingAlertProvider,
    private modalCtrl: ModalController,
    private userService: UserProvider
  ) {
    this.truck = navParams.get('truck');
    this.activePage = navParams.get('orderType');
    this.userService.getCurrentFillerShift();
    this.activePage === OrderType.Pending && this.startPollingForOrders();
    this.loadOrders();
    this.initSubscriptions();
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  startPollingForOrders() {
    interval(this.REFRESH_ORDERS_INTERVAL)
      .takeUntil(this.unsubscribe)
      .subscribe(() => this.getPendingOrders(true));
  }

  private loadOrders() {
    if (this.activePage == OrderType.Pending) {
      this.getTruckSchedule();
      this.title = 'Open Orders';
    } else if (this.activePage == OrderType.Completed) {
      this.getCompletedOrders();
      this.title = 'Completed Orders';
    }
  }

  private refreshData: Function;

  initSubscriptions() {
    let orders$;
    let loading$;

    switch (this.activePage) {
      case OrderType.Pending:
        orders$ = this.opsService.openOrders$;
        loading$ = this.opsService.loadingOpenOrders$;
        this.refreshData = () => this.getTruckSchedule();
        this.getOnTimeCompletedOrdersPercentage();
        break;
      case OrderType.Completed:
        orders$ = this.opsService.completedOrders$;
        loading$ = this.opsService.loadingCompletedOrders$;
        this.refreshData = () => this.getCompletedOrders();
        break;
      default:
        return;
    }

    orders$
      .takeUntil(this.unsubscribe)
      .subscribe(orders => (this.orders = orders));
    loading$.takeUntil(this.unsubscribe).subscribe(loading => {
      if (!loading && this.isLoading) {
        this.endRefresh();
      }
      if (this.page === 1) {
        this.isLoading = loading;
      }
    });

    this.userService.currentFillerShift$
      .takeUntil(this.unsubscribe)
      .subscribe(shift => (this.currentFillerShift = shift));
  }

  // Data

  loadData(infiniteScroll) {
    this.infiniteScroll = infiniteScroll;
    this.page += 1;
    this.refreshData();
  }

  private getTruckSchedule() {
    this.opsService.loadSelectedTruck();
    this.opsService.selectedTruck$.take(1).subscribe((truck: TruckModel) => {
      this.truck = truck;
      if (this.truck) {
        this.getPendingOrders();
      }
    });
  }

  private getPendingOrders(isPolling?: boolean) {
    this.opsService
      .getOrdersForTruck(this.truck, isPolling ? 1 : null, isPolling)
      .takeUntil(this.unsubscribe)
      .subscribe(
        result => {
          isPolling && this.showToastForResponseIfNeeded(result);
          this.markInfiniteScrollComplete();
          this.markRefresherComplete();
          if (!result.items.length) {
            this.enableInfiniteScroll(false);
          }
        },
        err => {
          if (err && err.status === 304) {
            // angular treats 304 as error.
          }
        }
      );
  }

  private getCompletedOrders() {
    this.opsService.getCompletedOrders().subscribe(result => {
      this.markInfiniteScrollComplete();
      this.markRefresherComplete();
      if (!result.items.length) {
        this.enableInfiniteScroll(false);
      }
    });
  }

  private enableInfiniteScroll(enable = true) {
    if (this.infiniteScroll) this.infiniteScroll.enable(enable);
  }

  private markInfiniteScrollComplete() {
    if (this.infiniteScroll) this.infiniteScroll.complete();
  }

  private markRefresherComplete() {
    this.refresher && this.refresher.complete();
    this.pullToRefresh && this.pullToRefresh.complete();
  }

  startDrivingAndSendETAs() {
    this.showEdit = false;
    let etaRequest = this.opsService.sendETAToOrders(this.selectedOrders);
    let drivingRequest = this.opsService.startDrivingToOrders(
      this.selectedOrders
    );
    this.loadingAlert.showLoader();
    Observable.combineLatest(etaRequest, drivingRequest)
      .finally(() => this.loadingAlert.dismissLoader())
      .subscribe(
        () => {
          this.loadOrders();
          this.loadingAlert.showToastConfirmation('Finished!');
        },
        () => {
          this.loadingAlert.showToastAlert('Error sending etas');
        }
      );
    this.selectedOrders = [];
  }

  startDriving() {
    this.showEdit = false;
    this.loadingAlert.showLoader();
    this.opsService
      .startDrivingToOrders(this.selectedOrders)
      .finally(() => this.loadingAlert.dismissLoader())
      .subscribe(
        () => {
          this.loadOrders();
          this.loadingAlert.showToastConfirmation('Finished!');
        },
        () => {
          this.loadingAlert.showToastAlert('Error updating order status');
        }
      );
    this.selectedOrders = [];
  }

  // Actions

  didRefresh(refresher: Refresher) {
    this.refresher = refresher;
    this.page = 1;
    this.enableInfiniteScroll();
    this.loadOrders();
  }

  private endRefresh() {
    if (this.refresher) {
      this.refresher.complete();
      this.refresher = null;
    }
  }

  didTapOrder(order: OpsOrderModel) {
    if (this.showEdit) {
      if (!this.orderIsSelected(order)) {
        this.selectedOrders.push(order);
      } else {
        this.selectedOrders.splice(this.selectedOrders.indexOf(order), 1);
      }
    } else {
      if (!this.currentFillerShift) {
        this.navCtrl.push(OrderDetailPage, { order: order, truck: this.truck });
      } else {
        this.navCtrl.push(OrderDetailPage, { order: order, truck: this.truck });
      }
    }
  }

  didTapSelect() {
    this.selectedOrders = [];
    this.showEdit = !this.showEdit;
  }

  didTapStartDriving() {
    let actionSheet = this.actionSheet.create({
      buttons: [
        {
          text: 'Start Driving',
          handler: () => this.startDriving(),
        },
        {
          text: 'Start Driving and Send ETAs',
          handler: () => this.startDrivingAndSendETAs(),
        },
        {
          text: 'Cancel',
          role: 'cancel',
        },
      ],
    });
    actionSheet.present();
  }

  didTapActionButton() {
    let modal = this.modalCtrl.create(ShiftPage);
    modal.present();
  }

  didTapOnCompletePercentage() {
    this.modalCtrl
      .create(OnTimePercentageModalComponent, {
        onTimePercentage: this.completedOnTimePercentage,
      })
      .present();
  }

  didTapSwitchToMain() {
    this.appMode.showModeSwitchingActionSheet();
  }

  // Helpers

  orderIsSelected(order) {
    return this.selectedOrders.indexOf(order) != -1;
  }

  displayNextUpForIndex(index) {
    return index === 0 && this.activePage === OrderType.Pending;
  }

  didTapStartDrivingAndSendEta(event: Event, order: OpsOrderModel) {
    event.stopPropagation();
    if (!this.currentFillerShift) {
      this.showClockInDisclaimerModal();
    } else {
      order.outsideTargetTimeWindow(new Date())
        ? this.showETAWarningModal(order)
        : this.startDrivingAndSendETA(order);
    }
  }

  showETAWarningModal(order: OpsOrderModel) {
    const warningModal = this.modalCtrl.create(EtaWarningModalComponent, null, {
      enableBackdropDismiss: false,
    });
    warningModal.onDidDismiss(proceed => {
      if (!proceed) {
        return;
      }
      this.startDrivingAndSendETA(order);
    });
    warningModal.present();
  }

  showClockInDisclaimerModal() {
    this.modalCtrl
      .create(SimpleMessageModalComponent, {
        title: 'clock in',
        description: 'Please start a shift before completing orders.',
        actionButtonText: 'Okay',
      })
      .present();
  }

  startDrivingAndSendETA(order: OpsOrderModel) {
    this.loadingAlert.showLoader();
    const reqSendETA = this.opsService.sendETAForOrder(order);
    const reqStartDriving = this.opsService.startDrivingToOrder(order);
    const requests = [reqSendETA, reqStartDriving];
    forkJoin(requests)
      .takeUntil(this.unsubscribe)
      .finally(() => this.loadingAlert.dismissLoader())
      .subscribe(() => {
        console.log('sent');
      });
  }

  // helpers

  scrollIfNeeded() {
    this.ordersList &&
      this.ordersList.scrollToTop(this.SCROLL_ANIMATION_DURATION);
    this.pullToRefresh && this.pullToRefresh._beginRefresh();
  }

  showToastForResponseIfNeeded(response) {
    if (
      response.status === 200 &&
      response.items.length &&
      response.pageInfo &&
      response.pageInfo.current_page === 1
    ) {
      this.showFloatingRefresh = true;
    }
  }

  didTapFloatingRefresh() {
    this.showFloatingRefresh = false;
    this.page = 1;
    this.scrollIfNeeded();
  }

  getOnTimeCompletedOrdersPercentage() {
    this.opsService.getCompletedOrders();
    this.opsService.completedOnTimePercentage$
      .takeUntil(this.unsubscribe)
      .subscribe(percentage => {
        this.completedOnTimePercentage = percentage;
      });
    this.opsService.completedOrdersThisShift$
      .takeUntil(this.unsubscribe)
      .subscribe(orders => {
        this.showOnTimePercentage = orders && orders.length > 0;
      });
  }
}



// WEBPACK FOOTER //
// ./src/pages/order-list/order-list.ts